import audio from './weapon-audio.json';
import type { ShotResult } from './types';

/** GameCharacter uses 25 updates per second; ShootBombAction counts map ticks. */
export const ORIGINAL_ACTION_FPS = 25;
export const ORIGINAL_ACTION_TICK_MS = 1000 / ORIGINAL_ACTION_FPS;
export const ORIGINAL_SHOOT_INTERVAL_TICKS = 24;
export const ORIGINAL_ACTION_COMPLETION_TICKS = 1;
export const ORIGINAL_ACTION_QUEUE_HANDOFF_TICKS = 1;
export const ORIGINAL_ATTACK_SEQUENCES = {
  0: { prepare: 'showthrows', attack: 'throw', hide: 'hidethrows', prepareFrames: [28, 29, 30, 30, 30], attackFrames: [31, 32, 33, 34, 35], hideFrames: [35] },
  1: { prepare: 'showgun', attack: 'shot', hide: 'hidegun', prepareFrames: [19, 20, 21, 21, 21], attackFrames: [22, 23, 26, 27], hideFrames: [27] },
} as const;

export function attackActionType(weaponId: string, kind: ShotResult['kind']): 0 | 1 {
  const mapping = audio as { weapons: Record<string, { normal: { actionType: number }; special: { actionType: number } }>; actions: Record<'fly' | 'ice', { actionType: number }> };
  const ball = kind === 'fly' || kind === 'ice' ? mapping.actions[kind] : mapping.weapons[weaponId]?.[kind === 'special' ? 'special' : 'normal'];
  return ball?.actionType === 1 ? 1 : 0;
}

/**
 * GamePlayer.__shoot queues PrepareShootAction, then ShootBombAction.
 * Both wait for actionPlaying() to stop before progressing; SHOT/THROWS
 * retain their final frame until the shoot interval expires, then HIDE.
 * GameCharacter.update draws the frame then increments _index. It only
 * clears actionPlaying on the following completion tick. ActionManager
 * marks Prepare finished on one execute, then shifts/prepares Shoot on its
 * next execute: joining just the frame-array lengths omits these ticks.
 * Evidence: GameCharacter.as:51-65,328-347; GamePlayer.as:739-759,1272;
 * PrepareShootAction.as:75-117; ShootBombAction.as:73-128,171.
 */
export function attackTiming(weaponId: string, kind: ShotResult['kind']) {
  const actionType = attackActionType(weaponId, kind);
  const sequence = ORIGINAL_ATTACK_SEQUENCES[actionType];
  const prepareFrameMs = sequence.prepareFrames.length * ORIGINAL_ACTION_TICK_MS;
  const attackFrameMs = sequence.attackFrames.length * ORIGINAL_ACTION_TICK_MS;
  const completionMs = ORIGINAL_ACTION_COMPLETION_TICKS * ORIGINAL_ACTION_TICK_MS;
  const queueHandoffMs = ORIGINAL_ACTION_QUEUE_HANDOFF_TICKS * ORIGINAL_ACTION_TICK_MS;
  const prepareMs = prepareFrameMs + completionMs + queueHandoffMs;
  const attackMs = attackFrameMs + completionMs;
  const holdMs = ORIGINAL_SHOOT_INTERVAL_TICKS * ORIGINAL_ACTION_TICK_MS;
  const hideMs = sequence.hideFrames.length * ORIGINAL_ACTION_TICK_MS;
  return { actionType, ...sequence, prepareFrameMs, attackFrameMs, completionMs, queueHandoffMs,
    prepareMs, attackMs, holdMs, hideMs,
    windupMs: prepareMs + attackMs, recoveryMs: holdMs + hideMs,
    totalMs: prepareMs + attackMs + holdMs + hideMs };
}

export function shotWindupMs(weaponId: string, kind: ShotResult['kind']): number {
  return attackTiming(weaponId, kind).windupMs;
}
